import BRANDS, { type BrandName } from "@elit-hair/brands";
import { Button, Icon, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbCalendarPlus } from "react-icons/tb";
import z from "zod";

import { PageActions } from "@/components/page-actions";
import { SearchBar } from "@/components/ui/search-bar";
import { EventTypeTable } from "@/pages/events/components/event-type-management-table";
import { BRAND_NAMES } from "@/queries/event-type.queries";
import { DEFAULT_DEBOUNCE_MS } from "@/utils/debounce";
import { useUrlSyncedTanstackPagination } from "@/utils/pagination";
import { APP_ROUTES_FULL } from "@/utils/routes";
import { searchValidator } from "@/utils/search-validator";

import { EventTypeActionDialog } from "./components/event-type-action-dialog";

const ALL_BRANDS = "all";

const isBrandName = (value: string): value is BrandName => value in BRANDS;

const EventTypesSearchSchema = z.object({
    page: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).optional(),
    search: z.string().optional(),
    brand: z
        .string()
        .optional()
        .transform((value) => (value && isBrandName(value) ? value : undefined)),
});

export const Route = createFileRoute(APP_ROUTES_FULL.EVENTS)({
    component: ManagementEventTypes,
    validateSearch: searchValidator(EventTypesSearchSchema),
});

function ManagementEventTypes() {
    const { t } = useTranslation();
    const navigate = Route.useNavigate();
    const searchParams = Route.useSearch({ structuralSharing: true });
    const { pageSize, pagination, onPaginationChange } = useUrlSyncedTanstackPagination({
        search: searchParams,
        navigate,
        to: Route.to,
    });

    const [openDialog, setOpenDialog] = useState(false);
    const [searchValue, setSearchValue] = useState(searchParams.search ?? "");

    useEffect(() => {
        setSearchValue(searchParams.search ?? "");
    }, [searchParams.search]);

    const updateFilters = useCallback(
        (updates: { search?: string; brand?: string }) => {
            navigate({
                to: Route.to,
                search: (prev) => {
                    const nextBrand = updates.brand === undefined ? prev.brand : updates.brand;

                    return {
                        ...prev,
                        search: updates.search !== undefined ? updates.search || undefined : prev.search,
                        brand: nextBrand && isBrandName(nextBrand) ? nextBrand : undefined,
                        page: undefined,
                    };
                },
                replace: true,
            });
        },
        [navigate]
    );

    return (
        <div className="flex flex-col gap-5">
            <PageActions>
                <SearchBar
                    value={searchValue}
                    onChange={setSearchValue}
                    placeholder={t("search_events")}
                    debounceMs={DEFAULT_DEBOUNCE_MS}
                    minLength={0}
                    onDebouncedChange={(value) => {
                        if (value === (searchParams.search ?? "")) return;
                        updateFilters({ search: value });
                    }}
                    className="w-full sm:w-[280px]"
                />
                <Select
                    value={searchParams.brand ?? ALL_BRANDS}
                    onValueChange={(value) => updateFilters({ brand: value })}
                >
                    <SelectTrigger className="w-48">
                        <SelectValue placeholder={t("select_brand")} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL_BRANDS}>{t("all_brands")}</SelectItem>
                        {BRAND_NAMES.map((brandKey) => {
                            const brandData = BRANDS[brandKey];
                            const displayName = brandData.displayName ?? brandData.name;
                            return (
                                <SelectItem key={brandKey} value={brandKey}>
                                    <div className="flex items-center gap-2">
                                        <img
                                            src={brandData.logoSquare}
                                            alt={displayName}
                                            className="size-4 rounded-sm"
                                        />
                                        <span>{displayName}</span>
                                    </div>
                                </SelectItem>
                            );
                        })}
                    </SelectContent>
                </Select>
                <Button onClick={() => setOpenDialog(true)}>
                    <Icon icon={TbCalendarPlus} />
                    {t("new_event")}
                </Button>
            </PageActions>

            <div className="flex-1 overflow-auto">
                <EventTypeTable
                    pageSize={pageSize}
                    pagination={pagination}
                    onPaginationChange={onPaginationChange}
                    search={searchParams.search}
                    brandName={searchParams.brand}
                />
            </div>

            <EventTypeActionDialog open={openDialog} onOpenChange={setOpenDialog} />
        </div>
    );
}
